import { fetchAuthToken } from "app/Auth";
import { MapModel } from "common/model";
import { Vector3D } from "common/types";
import { client } from "common/apiClient";
import {
  GeneratedMapData,
  PieceDraftSnapshot,
  ApiPieceListItem,
  ApiPieceComment,
  PieceRankingEntry,
  AiUsage,
  BuilderCommandId,
  CommandLimits,
  PieceAiMessage,
  Difficulty,
  PieceEditMeta,
  PieceEditHistoryResponse,
} from "features/builder/types";

export type { GeneratedMapData };

// ===========================================================================
// /v1/pieces/* 実接続（api-contract.md 準拠）
// 設計: API は status を返さず3軸フラグのみ。表示用 PieceStatus は呼び出し側で
// derivePieceStatus を使って導出する。
// ===========================================================================

// axios エラーに .status（HTTP ステータス）を正規化付与して再 throw
const normalizeError = (e: unknown): never => {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status) (e as { status?: number }).status = status;
  throw e;
};

// POST /v1/pieces/:piece_id/complete（spec.md §5.8.3 / api-contract.md）
export const completePiece = async (params: {
  piece_id: string;
  uid: string;
  tested: boolean;
}): Promise<{
  piece_id: string;
  completed: boolean;
  completed_at: string;
  published: boolean;
  version: number;
}> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).post(`/v1/pieces/${params.piece_id}/complete`, {
    uid: params.uid,
    tested: params.tested,
  });
  return res.data;
};

// POST /v1/pieces/:piece_id/publish
export const publishPiece = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ piece_id: string; published: boolean; version: number }> => {
  const token = await fetchAuthToken();
  // 保護者が公開を許可していない場合の 403 は再試行しても結果が変わらないため、リトライを止める
  const res = await client({ token }).post(
    `/v1/pieces/${params.piece_id}/publish`,
    { uid: params.uid },
    { "axios-retry": { retries: 0 } },
  );
  return res.data;
};

// DELETE /v1/pieces/:piece_id/publish（公開取り下げ）
export const unpublishPiece = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ piece_id: string; published: boolean }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).delete(
    `/v1/pieces/${params.piece_id}/publish?uid=${params.uid}`,
  );
  return res.data;
};

// POST /v1/pieces/:piece_id/draft（公開版を draft に完全スナップショットして編集開始）
export const createDraft = async (params: {
  piece_id: string;
  uid: string;
}): Promise<PieceDraftSnapshot> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).post(`/v1/pieces/${params.piece_id}/draft`, {
    uid: params.uid,
  });
  return res.data;
};

// DELETE /v1/pieces/:piece_id/draft（ドラフト破棄。公開版は不変）
export const deleteDraft = async (params: { piece_id: string; uid: string }): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).delete(`/v1/pieces/${params.piece_id}/draft?uid=${params.uid}`);
};

// GET /v1/pieces/:piece_id/draft（編集スナップショット一括取得）
export const getDraftSnapshot = async (params: {
  piece_id: string;
  uid: string;
}): Promise<PieceDraftSnapshot> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/${params.piece_id}/draft?uid=${params.uid}`);
  // draft 行由来の JSON カラムは環境により JSON 文字列のまま返ることがあるため
  // オブジェクトへ正規化する（getEditHistory の map/size と同じ防御）。
  // 文字列のままだと slice 側の Array.isArray 判定に落ちてヒント等が消える。
  const d = res.data;
  return {
    ...d,
    size: parseJsonField<Vector3D>(d.size) ?? d.size,
    map: parseJsonField<MapModel[]>(d.map) ?? [],
    enabled_commands: parseJsonField<string[]>(d.enabled_commands) ?? [],
    command_limits: parseJsonField<CommandLimits>(d.command_limits) ?? null,
    hint: parseJsonField<string[]>(d.hint) ?? ["", "", ""],
  };
};

// PUT /v1/pieces/:piece_id/commands（spec.md §5.8.7、編集中は draft 隔離）
// command_limits は { cmd: 1〜99 }。null = 全コマンド無制限
export const updatePieceCommands = async (params: {
  piece_id: string;
  uid: string;
  enabled_commands: BuilderCommandId[];
  command_limits: CommandLimits | null;
}): Promise<{ enabled_commands: BuilderCommandId[]; command_limits: CommandLimits | null }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).put(
    `/v1/pieces/${params.piece_id}/commands?uid=${params.uid}`,
    { enabled_commands: params.enabled_commands, command_limits: params.command_limits },
  );
  return res.data;
};

// PUT /v1/pieces/:piece_id（メタ：name/description/cover/background と、ピースごとの許可）
export const updatePieceMeta = async (params: {
  piece_id: string;
  uid: string;
  name?: string;
  description?: string;
  // cover・background は null で消す
  cover?: string | null;
  background?: string | null;
  enable_copied?: boolean;
  enable_comment?: boolean;
}): Promise<void> => {
  const { piece_id, uid, ...body } = params;
  const token = await fetchAuthToken();
  await client({ token }).put(`/v1/pieces/${piece_id}?uid=${uid}`, body);
};

// PUT /v1/pieces/:piece_id/capture（3Dマップのスクリーンショットをサムネイルに保存）
export const updatePieceCapture = async (params: {
  piece_id: string;
  uid: string;
  capture: string;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).put(`/v1/pieces/${params.piece_id}/capture?uid=${params.uid}`, {
    capture: params.capture,
  });
};

// PUT /v1/pieces/:piece_id/hints（作者が段階別ヒントを編集。編集中は draft 隔離）
export const updatePieceHints = async (params: {
  piece_id: string;
  uid: string;
  hints: string[];
}): Promise<{ hints: string[] }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).put(`/v1/pieces/${params.piece_id}/hints?uid=${params.uid}`, {
    hints: params.hints,
  });
  return res.data;
};

// GET /v1/pieces/:piece_id/hints
export const getPieceHints = async (params: { piece_id: string }): Promise<{ hints: string[] }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/${params.piece_id}/hints`);
  return res.data;
};

// GET /v1/pieces/:piece_id/commands - enabled_commands + command_limits 取得（所有者チェックなし、プレイ用）
export const getPieceCommands = async (params: {
  piece_id: string;
}): Promise<{ enabled_commands: BuilderCommandId[]; command_limits: CommandLimits | null }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/${params.piece_id}/commands`);
  return {
    ...res.data,
    command_limits: parseJsonField<CommandLimits>(res.data.command_limits) ?? null,
  };
};

// POST /v1/pieces/:piece_id/history（編集アクションを追記。バッチ送信は呼び出し側でバッファ）
// action は { action_type, payload?, map?, size?, capture?, meta? } を送れる（seq/version はサーバー採番）。
// map/size はそのアクション後のマップ状態スナップショット（履歴復元用）。capture・meta は区切りのときの画面と なまえなど
export const appendEditHistory = async (params: {
  piece_id: string;
  uid: string;
  actions: Array<{
    action_type: string;
    payload?: Record<string, unknown>;
    map?: MapModel[];
    size?: Vector3D;
    capture?: string;
    meta?: PieceEditMeta;
  }>;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).post(`/v1/pieces/${params.piece_id}/history`, {
    uid: params.uid,
    actions: params.actions,
  });
};

// サーバが JSON 文字列で返す map/size をオブジェクトへ整える（既に object なら素通し）
const parseJsonField = <T>(v: unknown): T | undefined => {
  if (v == null) return undefined;
  if (typeof v === "string") {
    try {
      return JSON.parse(v) as T;
    } catch {
      return undefined;
    }
  }
  return v as T;
};

// GET /v1/pieces/:piece_id/history
// map/size snapshot をパースして返す（クライアント側の履歴復元で使用）。見られるのは新しい区切り limit 個分
export const getEditHistory = async (params: {
  piece_id: string;
  version?: number;
  draft_id?: string;
}): Promise<PieceEditHistoryResponse> => {
  const token = await fetchAuthToken();
  // 既定の 500 件では、操作の多いピースで新しい行が切れる（古い順に返すため）。API の上限まで取る
  const query = new URLSearchParams({ limit: "2000" });
  if (params.version != null) query.set("version", String(params.version));
  if (params.draft_id) query.set("draft_id", params.draft_id);
  const qs = query.toString();
  const res = await client({ token }).get(
    `/v1/pieces/${params.piece_id}/history${qs ? `?${qs}` : ""}`,
  );
  const data = res.data as PieceEditHistoryResponse;
  return {
    ...data,
    entries: data.entries.map((e) => ({
      ...e,
      map: parseJsonField<MapModel[]>(e.map),
      size: parseJsonField<Vector3D>(e.size),
      meta: parseJsonField<PieceEditMeta>(e.meta),
    })),
  };
};

// POST /v1/pieces/ai/generate（新規 session + 初回生成、3案返却）
export const aiGenerate = async (params: {
  uid: string;
  piece_id: string;
  prompt: string;
  difficulty: Difficulty;
  size: { x: number; z: number };
  enabled_commands?: BuilderCommandId[];
  floors?: number;
}): Promise<{
  session_id: string;
  response_id: string;
  variations: GeneratedMapData[];
  text?: string;
  usage?: AiUsage;
}> => {
  const token = await fetchAuthToken();
  try {
    const res = await client({ token }).post("/v1/pieces/ai/generate", params);
    return res.data;
  } catch (e) {
    return normalizeError(e);
  }
};

// POST /v1/pieces/ai/refine（既存 session で改良。30日失効時は 410 → 呼び出し側で再 generate）
export const aiRefine = async (params: {
  uid: string;
  session_id: string;
  prompt: string;
  difficulty: Difficulty;
  size: { x: number; z: number };
  enabled_commands?: BuilderCommandId[];
  floors?: number;
}): Promise<{
  session_id: string;
  response_id: string;
  variations: GeneratedMapData[];
  text?: string;
}> => {
  const token = await fetchAuthToken();
  try {
    const res = await client({ token }).post("/v1/pieces/ai/refine", params);
    return res.data;
  } catch (e) {
    return normalizeError(e);
  }
};

// GET /v1/pieces/ai/sessions/:session_id/messages
export const getAiMessages = async (params: { session_id: string }): Promise<PieceAiMessage[]> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/ai/sessions/${params.session_id}/messages`);
  return res.data;
};

// PUT /v1/pieces/user/:uid/tutorial/:step_id（spec.md §5.8.6）
export const saveTutorialProgress = async (params: {
  uid: string;
  step_id: number;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).put(`/v1/pieces/user/${params.uid}/tutorial/${params.step_id}`);
};

// GET /v1/pieces/user/:uid/tutorial（完了 step_id 配列）
export const getTutorialProgress = async (params: {
  uid: string;
}): Promise<{ steps: number[] }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/user/${params.uid}/tutorial`);
  return res.data;
};

// POST /v1/pieces/:piece_id/play（テストプレイ完了）
// GET /v1/pieces/:piece_id/ranking - クリア記録のランキング（手数が少ない順）
export const getPieceRanking = async (params: {
  piece_id: string;
  limit?: number;
}): Promise<PieceRankingEntry[]> => {
  const token = await fetchAuthToken();
  const qs = params.limit != null ? `?limit=${params.limit}` : "";
  const res = await client({ token }).get(`/v1/pieces/${params.piece_id}/ranking${qs}`);
  return res.data;
};

export const playPiece = async (params: {
  piece_id: string;
  uid: string;
  cleared: boolean;
  moves?: number;
  time_ms?: number;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).post(`/v1/pieces/${params.piece_id}/play`, {
    uid: params.uid,
    cleared: params.cleared,
    moves: params.moves,
    time_ms: params.time_ms,
  });
};

// POST /v1/pieces/:piece_id/view（閲覧記録）
export const viewPiece = async (params: { piece_id: string; uid?: string }): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).post(`/v1/pieces/${params.piece_id}/view`, { uid: params.uid });
};

// PUT /v1/pieces/user/:uid/favorite/:piece_id（いいねトグル）
export const toggleFavorite = async (params: {
  uid: string;
  piece_id: string;
}): Promise<{ is_liked: boolean }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).put(
    `/v1/pieces/user/${params.uid}/favorite/${params.piece_id}`,
  );
  return res.data;
};

// PUT /v1/pieces/user/:uid/reaction/:piece_id - ピース6種リアクションのトグル
export const reactToPiece = async (params: {
  uid: string;
  piece_id: string;
  reaction: string;
}): Promise<{
  piece_id: string;
  reaction: string;
  active: boolean;
  reactions: {
    difficult: number;
    easy: number;
    cool: number;
    fun: number;
    amazing: number;
    bad: number;
  };
}> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).put(
    `/v1/pieces/user/${params.uid}/reaction/${params.piece_id}`,
    { reaction: params.reaction },
  );
  return res.data;
};

// GET /v1/pieces/:piece_id - 詳細（my_reactions・最新 reaction を含む）
export const getPieceDetail = async (params: {
  piece_id: string;
  uid?: string;
}): Promise<ApiPieceListItem> => {
  const token = await fetchAuthToken();
  const qs = params.uid ? `?uid=${params.uid}` : "";
  const res = await client({ token }).get(`/v1/pieces/${params.piece_id}${qs}`);
  return res.data;
};

// ---- 作成 / コンテンツ保存 ----

// POST /v1/pieces - 新規ピース作成（作りかけ）。作成された PieceListItem を返す
export const createPiece = async (params: {
  uid: string;
  name?: string;
  description?: string;
  size?: { x: number; y: number; z: number };
  map?: unknown[];
  enabled_commands?: BuilderCommandId[];
}): Promise<ApiPieceListItem> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).post("/v1/pieces", params);
  return res.data;
};

// PUT /v1/pieces/:piece_id/content - 座標データ保存（編集中は draft 隔離）
export const savePieceContent = async (params: {
  piece_id: string;
  uid: string;
  size: { x: number; y: number; z: number };
  map: unknown[];
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).put(`/v1/pieces/${params.piece_id}/content?uid=${params.uid}`, {
    size: params.size,
    map: params.map,
  });
};

// GET /v1/pieces/:piece_id/content - 座標データ取得
export const getPieceContent = async (params: {
  piece_id: string;
}): Promise<{
  piece_id: string;
  size: { x: number; y: number; z: number };
  block_count: number;
  map: unknown[];
  updated_at: string;
}> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/${params.piece_id}/content`);
  return res.data;
};

// ---- 一覧 ----

// GET /v1/pieces/user/:uid - 自分のピース一覧（削除済み除く）
export const getMyPieces = async (uid: string): Promise<ApiPieceListItem[]> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/user/${uid}`);
  return res.data;
};

// GET /v1/pieces/user/:uid/trash - ゴミ箱一覧
export const getTrashPieces = async (uid: string): Promise<ApiPieceListItem[]> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).get(`/v1/pieces/user/${uid}/trash`);
  return res.data;
};

// GET /v1/pieces/public - みんなのピース一覧（公開中、ページング）
export const getPublicPieces = async (params: {
  uid?: string;
  sort?: string;
  order?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiPieceListItem[]> => {
  const token = await fetchAuthToken();
  const q = new URLSearchParams();
  if (params.uid) q.set("uid", params.uid);
  if (params.sort) q.set("sort", params.sort);
  if (params.order) q.set("order", params.order);
  if (params.keyword) q.set("keyword", params.keyword);
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  const qs = q.toString();
  const res = await client({ token }).get(`/v1/pieces/public${qs ? `?${qs}` : ""}`);
  return res.data;
};

// ---- ライフサイクル（一覧操作）----

// DELETE /v1/pieces/:piece_id - 論理削除（ゴミ箱へ）
export const trashPiece = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ piece_id: string; deleted_at: string }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).delete(`/v1/pieces/${params.piece_id}?uid=${params.uid}`);
  return res.data;
};

// POST /v1/pieces/:piece_id/restore - ゴミ箱から復元
export const restorePiece = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ piece_id: string; completed: boolean; published: boolean }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).post(`/v1/pieces/${params.piece_id}/restore`, {
    uid: params.uid,
  });
  return res.data;
};

// DELETE /v1/pieces/:piece_id/completely - 物理削除
export const deletePieceForever = async (params: {
  piece_id: string;
  uid: string;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).delete(`/v1/pieces/${params.piece_id}/completely?uid=${params.uid}`);
};

// POST /v1/pieces/:piece_id/copy - フォーク（コピー）
export const copyPiece = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ piece_id: string; copied_piece_id: string }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).post(`/v1/pieces/${params.piece_id}/copy`, {
    uid: params.uid,
  });
  return res.data;
};

// ---- コメント（みんなのピース）----

// GET /v1/pieces/:piece_id/comments - トップコメント一覧（新しい順）
export const getComments = async (params: {
  piece_id: string;
  uid?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiPieceComment[]> => {
  const token = await fetchAuthToken();
  const q = new URLSearchParams();
  if (params.uid) q.set("uid", params.uid);
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  const qs = q.toString();
  const res = await client({ token }).get(
    `/v1/pieces/${params.piece_id}/comments${qs ? `?${qs}` : ""}`,
  );
  return res.data;
};

// GET /v1/pieces/:piece_id/comments/:comment_id/replies - 返信一覧（古い順）
export const getReplies = async (params: {
  piece_id: string;
  comment_id: string;
  uid?: string;
}): Promise<ApiPieceComment[]> => {
  const token = await fetchAuthToken();
  const qs = params.uid ? `?uid=${params.uid}` : "";
  const res = await client({ token }).get(
    `/v1/pieces/${params.piece_id}/comments/${params.comment_id}/replies${qs}`,
  );
  return res.data;
};

// POST /v1/pieces/:piece_id/comments - コメント/返信を投稿
export const postComment = async (params: {
  piece_id: string;
  uid: string;
  content: string;
  parent_comment_id?: string;
}): Promise<{
  id: string;
  piece_id: string;
  parent_comment_id: string | null;
  created_at: string;
}> => {
  const { piece_id, ...body } = params;
  const token = await fetchAuthToken();
  const res = await client({ token }).post(`/v1/pieces/${piece_id}/comments`, body);
  return res.data;
};

// DELETE /v1/pieces/:piece_id/comments/:comment_id - 作者によるソフト削除
export const deleteComment = async (params: {
  piece_id: string;
  comment_id: string;
  uid: string;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).delete(
    `/v1/pieces/${params.piece_id}/comments/${params.comment_id}?uid=${params.uid}`,
  );
};

// PUT /v1/pieces/user/:uid/comments/:comment_id/reaction - リアクショントグル
export const reactToComment = async (params: {
  uid: string;
  comment_id: string;
  reaction: string;
}): Promise<{ comment_id: string; reaction: string; active: boolean; reactions: unknown }> => {
  const token = await fetchAuthToken();
  const res = await client({ token }).put(
    `/v1/pieces/user/${params.uid}/comments/${params.comment_id}/reaction`,
    { reaction: params.reaction },
  );
  return res.data;
};

// POST /v1/pieces/:piece_id/report - ピース本体の通報受付
export const reportPiece = async (params: {
  piece_id: string;
  uid: string;
  reason: string;
  detail?: string;
}): Promise<void> => {
  const token = await fetchAuthToken();
  // ゲスト・未登録の 403 は再送しても結果が変わらないため、リトライを止める
  await client({ token }).post(
    `/v1/pieces/${params.piece_id}/report`,
    { uid: params.uid, reason: params.reason, detail: params.detail },
    { "axios-retry": { retries: 0 } },
  );
};

// POST /v1/pieces/:piece_id/comments/:comment_id/report - 通報受付
export const reportComment = async (params: {
  piece_id: string;
  comment_id: string;
  uid: string;
  reason: string;
  detail?: string;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).post(
    `/v1/pieces/${params.piece_id}/comments/${params.comment_id}/report`,
    { uid: params.uid, reason: params.reason, detail: params.detail },
  );
};
